import { action, observable, computed } from 'mobx'
import { Api, parseToCatalog } from 'utilities'
import {
  RootStore, EDITOR_MONITOR_STORE, GENERAL_STORE,
  EDITOR_SHOTS_STORE, EDITOR_GROUPS_STORE, EDITOR_LOCATIONS_STORE,
  EDITOR_INTERACTIVE_AREAS_STORE, EDITOR_SPRITE_FRAMES_STORE
} from 'store'
import { Person, Project, Brand, EntityClassTypes, Product, InteractiveAreaAPI } from 'models'
import { generatePrompt, generateStringVariations, getProductsFromSession } from 'components/editor/Monitor/VideoArea/InteractiveAreaCanvas/utils'

export const EDITOR_STORE = 'EDITOR_STORE'

export class EditorStore {
  rootStore: RootStore

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  @observable projectName: string = ''
  @observable projectId: number = null
  @observable videoEntityId: number = null
  @observable frames: number = null
  @observable videoWidth: number = null
  @observable videoHeight: number = null
  @observable actors: Person[] = []
  @observable directors: Person[] = []
  @observable producers: Person[] = []
  @observable brands: Brand[] = []

  @observable project: Project = {} as Project

  private setProject (project) {
    this.project = project
  }

  @computed get personsCatalog () {
    const personsCatalog = {}

    parseToCatalog(this.actors, personsCatalog)
    parseToCatalog(this.directors, personsCatalog)
    parseToCatalog(this.producers, personsCatalog)

    return personsCatalog
  }

  @computed get brandsCatalog () {
    return parseToCatalog(this.brands)
  }

  @computed get productsCatalog () {
    const productsCatalog = {}
    this.brands.forEach(brand => {
      parseToCatalog(brand.products, productsCatalog)
    })
    return productsCatalog
  }

  @action.bound clearEditorState () {
    this.projectName = ''
    this.setProjectId()
    this.setVideoEntityId()
    this.frames = null
    this.actors = []
    this.directors = []
    this.producers = []
    this.brands = []

    this.rootStore[EDITOR_SHOTS_STORE].clearEditorShotsState()
    this.rootStore[EDITOR_GROUPS_STORE].clearEditorGroupsState()
    this.rootStore[EDITOR_LOCATIONS_STORE].clearEditorLocationState()
    this.rootStore[EDITOR_MONITOR_STORE].clearEditorMonitorState()
    this.rootStore[EDITOR_SPRITE_FRAMES_STORE].clearLoadedImages()
  }

  @action.bound openForEdit (projectId) {
    // TODO : check this
    // try {
    //   if (this.projectId && Number(projectId) === this.projectId) return
    // } catch (err) {
    //   console.error(err)
    //   return
    // }

    const {
      [GENERAL_STORE]: { handleApiError, setLoadingOn, collapseSideMenu, showInfo },
      [EDITOR_SHOTS_STORE]: { handleInitialShots },
      [EDITOR_INTERACTIVE_AREAS_STORE]: { setInitialInteractiveAreas }
    } = this.rootStore

    this.clearEditorState()
    setLoadingOn()
    collapseSideMenu()

    Api
      .get(`/video/${projectId}/openForEdit`)
      .then((project: any) => {
        const {
          shotGroups, id, frames, interactiveAreas, name,
          width, height, videoEntityId
        } = project
        this.updateProjectAndEntities(project)
        this.projectName = name
        this.setProjectId(id)
        this.frames = frames
        this.videoWidth = width || 1280
        this.videoHeight = height || 720
        this.setVideoEntityId(videoEntityId)

        if (!width || !height) {
          showInfo(
            'The Width and Height of the original video are not available. Location and size of the Interactive areas may not be accurate.'
          )
        }

        setInitialInteractiveAreas(interactiveAreas)
        handleInitialShots(shotGroups)
        this.preFetchProducts()
      })
      .catch(handleApiError)
  }

  @action.bound setVideoEntityId (id = null) {
    try {
      this.videoEntityId = id === null ? id : Number(id)
    } catch (error) {
      console.error(error)
      this.rootStore[GENERAL_STORE].showError(`Video Entity ID is invalid. (id: ${id})`)
    }
  }

  @action.bound setProjectId (id = null) {
    if (typeof id === 'string') {
      try {
        id = Number(id)
      } catch (error) {
        console.error(error)
      }
    }

    this.projectId = id
  }

  @action.bound updateProjectAndEntities (project) {
    this.setProject(project)
    const { actors = [], directors = [], producers = [], brands = [] } = project
    this.actors = actors
    this.directors = directors
    this.producers = producers
    this.brands = brands
    // When add or remove product need to revalidate
    this.preFetchProducts()
  }

  @action.bound addEntitiesLocally (
    { actors = [], directors = [], producers = [], brands = [] }
  ) {
    const addPersons = (persons, key) => {
      const cloneEntities = [...this[key]]
      const newEntities = persons
        .filter(p => !cloneEntities.find(entity => entity.id === p.id))
      this[key] = [
        ...cloneEntities,
        ...newEntities.map(entity => ({ ...entity, shotsCount: entity.shotsCount || 0 }))
      ]
    }

    const haveAddedActors = actors.length > this.actors.length
    const haveAddedDirectors = directors.length > this.directors.length
    const haveAddedProducers = producers.length > this.producers.length

    haveAddedActors && addPersons(actors, 'actors')
    haveAddedDirectors && addPersons(directors, 'directors')
    haveAddedProducers && addPersons(producers, 'producers')

    let products = []
    this.brands.forEach(brand => { products = products.concat(brand.products || []) })

    let newProducts = []
    brands.forEach(brand => { newProducts = newProducts.concat(brand.products || []) })

    const haveAddedProducts = newProducts.length > products.length

    if (haveAddedProducts) {
      const cloneBrands = [...this.brands]

      const productsToAdd = newProducts
        .filter(p => !products.find(product => product.id === p.id))

      productsToAdd.forEach(product => {
        const productToAdd = { ...product, shotsCount: product.shotsCount || 0 }
        const brandIndex = cloneBrands.findIndex(b => b.id === product.brandId)
        if (brandIndex === -1) {
          const newBrand = {
            ...brands.find(b => b.id === product.brandId),
            products: [productToAdd]
          }
          cloneBrands.push(newBrand)
        } else {
          cloneBrands[brandIndex].products.push(productToAdd)
        }
      })

      this.brands = cloneBrands
    }
  }

  addEntities = entities => {
    const body = {
      id: this.projectId,
      ...entities
    }

    Api
      .post('/project/addEntities', body)
      .then(this.addEntitiesLocally)
      .catch(this.rootStore[GENERAL_STORE].handleApiError)
  }

  clearSelectedShotsAndGroups = () => {
    const {
      [EDITOR_SHOTS_STORE]: { clearSelectedShots },
      [EDITOR_GROUPS_STORE]: { clearSelectedGroups }
    } = this.rootStore

    clearSelectedShots()
    clearSelectedGroups()
  }

  getPersonById = id => {
    return this.personsCatalog[id]
  }

  getProductById = id => {
    return this.productsCatalog[id]
  }

  openShotsWizard = () => {
    this.rootStore[GENERAL_STORE].push(`/editor/${this.projectId}/shots/${this.videoEntityId}`)
  }

  openTimelineTagEditor = () => {
    this.rootStore[GENERAL_STORE]
      .push(`/editor/${this.projectId}/timeline-tags/${this.videoEntityId}`)
  }

  getProjectDetails = (projectId) => {
    const {
      [GENERAL_STORE]: { handleApiError }
    } = this.rootStore

    Api
      .get(`/video/${projectId}/openForEdit`)
      .then((project: any) => {
        this.setProject({ ...this.project, ...project })
      })
      .catch(handleApiError)
  }

  generateInteractiveArea = () => {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: { generateAutoInteractiveAreas }
    } = this.rootStore
    return generateAutoInteractiveAreas(this.project)
  }

  preFetchProducts = () => {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: { preloadProducts }
    } = this.rootStore
    const data = preloadProducts(this.project)
    // For storing data
    sessionStorage.setItem('preloadProducts', JSON.stringify(data))
  }

  generateBox = async () => {
    const {
      [GENERAL_STORE]: { handleApiError },
      [EDITOR_MONITOR_STORE]: { descaleRectPayload },
      [EDITOR_INTERACTIVE_AREAS_STORE]: { saveIA }
    } = this.rootStore

    const prompt = generatePrompt()
    const { scaleX, scaleY, freeSpaceAroundVideo } = descaleRectPayload()
    const brandExternalId = this.brands[0]?.externalId

    return await Api.post(`/video/v2/${this.videoEntityId}/generate-box`,
      { prompt, scaleX, scaleY, freeSpaceAroundVideo, brandExternalId: Number(brandExternalId) }
    ).catch(handleApiError)

    // if (res) {
    //   // Save Interactive area
    //   await Promise.all((res as InteractiveAreaAPI[]).map(iA => saveIA(iA)))
    //   return true
    // }

    // return false
  }

  updateZIndex = async () => {
    const {
      [GENERAL_STORE]: { handleApiError }
    } = this.rootStore

    return Api.get(`/video/${this.videoEntityId}/updateZindex`).catch(handleApiError)
  }

  recycleInteractiveArea = async () => {
    const {
      [GENERAL_STORE]: { handleApiError }
    } = this.rootStore

    return Api.get(`/video/${this.videoEntityId}/delete`).catch(handleApiError)
  }

  attachSuggestProducts = async () => {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        interactiveAreas
      },
      [EDITOR_MONITOR_STORE]: {
        assignEntityToIA
      }
    } = this.rootStore

    // Get products from session
    const products = getProductsFromSession()

    // Attach product to interactiveArea
    Promise.allSettled(
      Object.values(interactiveAreas).map(iA => {
        const { id, name, entityClass } = iA
        let entityId: number

        for (let i = 0; i < generateStringVariations(name).length; i++) {
          const updatedName = generateStringVariations(name)[i]

          entityId = products[updatedName || ''] && Object.values(products[updatedName || ''])?.length && (Object.values(products[updatedName || ''])[0] as Product[])?.length && Object.values(products[updatedName || ''])[0][0]?.id as number

          if (entityId) break
        }

        if (!entityId || entityClass) return

        assignEntityToIA(entityId, EntityClassTypes.PRODUCT, id)
      })
    )
  }

  getProjectDetailsById = (project : Project) : Promise<Project> => {
    return Api.get(`/video/${project.id}/openForEdit`)
  }
}
